<template>
  <RevCard class="p-16">
    <h3 class="heading-3 mb-16">
      {{ title }}
    </h3>

    <div class="mb-16 flex flex-row">
      <IconHome class="mr-8 mt-8" />
      <div
        class="group relative w-full p-8 hover:bg-static-default-mid hover:rounded-sm hover:cursor-pointer"
      >
        <div class="absolute right-0 top-0 hidden group-hover:block">
          <RevTooltip v-if="!isAddressCopied">
            <span class="whitespace-nowrap">
              {{ i18n(translations.copyAddress) }}
            </span>
            <template #trigger="{ show, hide }">
              <RevButtonIcon
                class="mr-4 mt-4"
                :icon="IconCopy"
                tabindex="0"
                variant="secondary"
                @click="handleCopyAddress"
                @focusin="show"
                @focusout="hide"
              />
            </template>
          </RevTooltip>
          <IconCheckLarge
            v-if="isAddressCopied"
            class="absolute right-0 top-0 m-12"
            size="medium"
          />
        </div>

        <div v-if="addressInfo.company">
          {{
            i18n(translations.company, {
              companyName: addressInfo.company,
            })
          }}
        </div>

        <div>
          {{
            i18n(translations.fullName, {
              firstName: addressInfo.firstName,
              lastName: addressInfo.lastName,
            })
          }}
        </div>

        <div
          v-if="
            addressInfo.firstNamePronunciation &&
            addressInfo.lastNamePronunciation
          "
        >
          {{
            i18n(translations.fullName, {
              firstName: addressInfo.firstNamePronunciation,
              lastName: addressInfo.lastNamePronunciation,
            })
          }}
        </div>

        <div>{{ addressInfo.street }}</div>

        <div>{{ addressInfo.street2 }}</div>

        <div>
          {{ addressInfo.state }} {{ addressInfo.postalCode }}
          {{ addressInfo.city }}
        </div>

        <div class="flex flex-row items-center">
          <Flag class="mr-8" :country-code="addressInfo.country" />
          {{ i18n.country(addressInfo.country) }}
        </div>
      </div>
    </div>

    <div class="mb-16 flex flex-row">
      <IconSmartphone class="mr-8" />

      <div class="px-8">{{ addressInfo.phoneNumber || '-' }}</div>
    </div>

    <div class="mb-16 flex flex-row">
      <IconMail class="mr-8" />

      <div class="px-8">{{ email || '-' }}</div>
    </div>

    <div v-if="fiscalIdentificationNumber" class="mb-16 flex flex-row">
      <div class="mr-2">
        {{ i18n(translations.fiscalIdentificationNumber) }}
      </div>

      <div>{{ fiscalIdentificationNumber }}</div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { AddressType } from '@backmarket/http-api/src/api-models/Address'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconCheckLarge } from '@ds/icons/IconCheckLarge'
import { IconCopy } from '@ds/icons/IconCopy'
import { IconHome } from '@ds/icons/IconHome'
import { IconMail } from '@ds/icons/IconMail'
import { IconSmartphone } from '@ds/icons/IconSmartphone'

import translations from './Address.translations'

const props = defineProps<{
  title: string
  email: string
  addressInfo: AddressType & { state: string; phoneNumber: string }
  fiscalIdentificationNumber?: string
}>()

const i18n = useI18n()

const isAddressCopied = ref(false)

const formattedAddress = computed(() => {
  const rows = [
    `${props.addressInfo.company ?? ''}`,
    `${props.addressInfo.firstName} ${props.addressInfo.lastName}`,
    `${props.addressInfo.street}`,
    `${props.addressInfo.street2 ?? ''}`,
    `${props.addressInfo.state ?? ''} ${props.addressInfo.postalCode} ${props.addressInfo.city}`,
    `${i18n.country(props.addressInfo.country)}`,
  ]

  return Object.values(rows)
    .filter((addressElement) => addressElement.trim().length > 0)
    .map((addressElement) => addressElement.trim())
    .join('\n')
})

async function handleCopyAddress() {
  try {
    await navigator.clipboard.writeText(formattedAddress.value)
    isAddressCopied.value = true

    setTimeout(() => {
      isAddressCopied.value = false
    }, 800)
  } catch {
    // fails silently
  }
}
</script>
